<script setup lang="ts">
import { ref, computed, watch, onUnmounted, inject } from 'vue'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import { useOpsStore } from '@/stores/ops'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const incidentId = inject<string>('incidentId')!
const ptiStore = usePriorityTrafficStore()
const opsStore = useOpsStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const canResolve = computed(() => permissionsStore.can('priority_traffic.resolve', opRole.value))

// ─── Elapsed timer ────────────────────────────────────────────────────────────

const elapsed = ref('0:00')
let timer: ReturnType<typeof setInterval> | null = null

watch(
  () => ptiStore.activePti,
  (pti) => {
    if (timer) clearInterval(timer)
    timer = null
    if (pti?.declared_at) {
      timer = setInterval(() => {
        const secs = Math.floor((Date.now() - pti.declared_at.toDate().getTime()) / 1000)
        elapsed.value = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
      }, 1000)
    } else {
      elapsed.value = '0:00'
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// ─── Resolve dialog ───────────────────────────────────────────────────────────

const showResolveDialog = ref(false)

async function confirmResolve() {
  const pti = ptiStore.activePti
  const opId = opsStore.selectedOpId
  if (!pti || !opId) return
  await ptiStore.resolvePti(incidentId, opId, pti.pti_id)
  showResolveDialog.value = false
}
</script>

<template>
  <div
    v-if="ptiStore.activePti"
    class="border border-amber-400 rounded bg-amber-50 dark:bg-amber-950/20 pti-alert-pulse overflow-hidden"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 bg-amber-500/10 border-b border-amber-400/50">
      <div class="flex items-center gap-2.5">
        <span class="w-2.5 h-2.5 rounded-full bg-amber-500 pti-dot-pulse shrink-0" />
        <div>
          <p class="font-headline font-black text-sm uppercase tracking-tight text-amber-800 dark:text-amber-300">
            Priority Traffic
          </p>
          <p class="font-mono text-xs text-amber-700/70 dark:text-amber-400/70 mt-0.5">
            Team {{ ptiStore.activePti.declared_by_team_identifier }} has reported a find
          </p>
        </div>
      </div>
      <span class="font-mono text-2xl font-bold text-amber-600 dark:text-amber-400 tabular-nums leading-none">
        {{ elapsed }}
      </span>
    </div>

    <!-- Details -->
    <div class="px-4 py-4 flex flex-col gap-4">
      <!-- Location -->
      <div>
        <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-2">Location</p>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-0.5">
            <span class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Address / Assignment</span>
            <span
              :class="[
                'font-mono text-sm',
                ptiStore.activePti.location_address ? 'text-on-surface' : 'text-outline italic',
              ]"
            >
              {{ ptiStore.activePti.location_address ?? 'Not recorded' }}
            </span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Coordinates</span>
            <span
              :class="[
                'font-mono text-sm',
                ptiStore.activePti.location_coordinates ? 'text-on-surface' : 'text-outline italic',
              ]"
            >
              {{ ptiStore.activePti.location_coordinates ?? 'Not recorded' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Resources -->
      <div>
        <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-2">Resources Requested</p>
        <div v-if="ptiStore.activePti.resources_requested.length > 0" class="flex flex-wrap gap-1.5">
          <span
            v-for="resource in ptiStore.activePti.resources_requested"
            :key="resource"
            class="inline-flex items-center px-2.5 py-1 bg-amber-500 text-white font-mono text-xs rounded font-bold"
          >
            {{ resource }}
          </span>
        </div>
        <p v-else class="font-mono text-sm text-outline italic">
          No resources recorded yet
        </p>
      </div>

      <!-- Notes -->
      <div v-if="ptiStore.activePti.notes">
        <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1">Notes</p>
        <p class="font-mono text-sm text-on-surface">{{ ptiStore.activePti.notes }}</p>
      </div>

      <!-- Resolve -->
      <div class="pt-1">
        <PermissionGuard action="priority_traffic.resolve" :role="opRole">
          <FosButton variant="secondary" @click="showResolveDialog = true">
            Resolve Priority Traffic
          </FosButton>
        </PermissionGuard>
      </div>
    </div>
  </div>

  <FosAlertDialog
    :open="showResolveDialog"
    title="Resolve Priority Traffic"
    description="Mark this priority traffic incident as resolved. The alert will be dismissed."
    confirm-label="Resolve"
    @update:open="showResolveDialog = $event"
    @confirm="confirmResolve"
  />
</template>

<style scoped>
.pti-alert-pulse {
  animation: alert-pulse 2s ease-in-out infinite;
}

@keyframes alert-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
  50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.2); }
}

.pti-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}
</style>
